import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

// Reuse same base URL logic used elsewhere
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  // If no env URL, pick sensible defaults
  if (!envUrl) {
    // If app is opened via file:// (no hostname), go directly to Cloud Run to ensure requests are made
    const protocol = typeof window !== 'undefined' ? window.location.protocol : '';
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    if (protocol !== 'http:' && protocol !== 'https:') {
      return 'https://flask-router-340481947694.europe-west1.run.app';
    }
    // Otherwise, in production rely on same-origin rewrites; in dev use Cloud Run default
    return process.env.NODE_ENV === 'production'
      ? ''
      : 'https://flask-router-340481947694.europe-west1.run.app';
  }
  // If it's a placeholder URL, use localhost for development, same-origin in production
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  // If env URL contains Cloud Run domain, always use it
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  // In production builds, always use the configured env URL
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  // In development, use localhost unless explicitly set to Cloud Run
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

// Debounce utility
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default function CityAutocomplete({
  value,
  onChange,
  placeholder = 'Type your city (Hebrew or English)',
  inputId = 'city-autocomplete',
  required = false,
  enforceSelection = false,
  onValidityChange,
  onSelectionComplete
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const lastFetchQueryRef = useRef('');
  const [selectedValue, setSelectedValue] = useState('');
  const [isValidSelection, setIsValidSelection] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [inputRect, setInputRect] = useState(null);
  const [viewportOffset, setViewportOffset] = useState({ top: 0, left: 0 });
  const [viewportHeight, setViewportHeight] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const listRef = useRef(null);

  // Debounce the query for API calls (300ms delay)
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    const next = value || '';
    setQuery(next);
    // When a non-empty value is provided from outside (e.g. editing an
    // existing job), treat it as a valid pre-selected city so the popup
    // doesn't auto-open but the button still shows the saved location.
    if (next) {
      setSelectedValue(next);
      setIsValidSelection(true);
    } else {
      setSelectedValue('');
      setIsValidSelection(false);
    }
  }, [value]);

  useEffect(() => {
    if (typeof onValidityChange === 'function') {
      onValidityChange(isValidSelection);
    }
  }, [isValidSelection, onValidityChange]);

  // Auto-focus mobile input when sheet opens
  useEffect(() => {
    if (open && isMobile && mobileInputRef.current) {
      // Small delay to ensure the sheet is rendered
      setTimeout(() => {
        if (mobileInputRef.current) {
          mobileInputRef.current.focus();
        }
      }, 100);
    }
  }, [open, isMobile]);

  useEffect(() => {
    const onClickOutside = (e) => {
      const withinInput = containerRef.current && containerRef.current.contains(e.target);
      const withinList = listRef.current && listRef.current.contains(e.target);
      if (!withinInput && !withinList) {
        setOpen(false);
        setHighlightIdx(-1);
      }
    };
    const onTouchOutside = (e) => {
      const withinInput = containerRef.current && containerRef.current.contains(e.target);
      const withinList = listRef.current && listRef.current.contains(e.target);
      if (!withinInput && !withinList) {
        setOpen(false);
        setHighlightIdx(-1);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('touchstart', onTouchOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('touchstart', onTouchOutside);
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobile(mq.matches);
    handler();
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, []);

  // Prevent background scroll when mobile sheet is open
  useEffect(() => {
    if (!(open && isMobile)) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open, isMobile]);

  // Track visual viewport offsets (iOS keyboard/zoom)
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) {
      // Fallback for browsers without visual viewport API
      setViewportHeight(window.innerHeight);
      return;
    }
    const update = () => {
      setViewportOffset({
        top: vv.offsetTop || 0,
        left: vv.offsetLeft || 0
      });
      setViewportHeight(vv.height || window.innerHeight);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  useEffect(() => {
    if (!open || !isMobile || !inputRef.current) return;
    const measure = () => {
      const rect = inputRef.current.getBoundingClientRect();
      setInputRect(rect ? { top: rect.bottom, left: rect.left, width: rect.width } : null);
    };
    measure();
    const onScroll = () => measure();
    const onResize = () => measure();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, isMobile, query, suggestions.length, viewportOffset.top, viewportOffset.left]);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.trim().length < 1) {
      setSuggestions([]);
      setOpen(false);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      // Track last attempted query (for debugging and to avoid racey UI)
      lastFetchQueryRef.current = q;
      const resp = await axios.get(`${API_BASE_URL}/api/location/israel-cities`, {
        params: { q: q.trim(), limit: 20 }
      });
      setSuggestions(resp.data?.cities || []);
      setOpen(true);
      setHighlightIdx(-1);
    } catch (e) {
      console.error('Error fetching city suggestions:', e);
      setSuggestions([]);
      setOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch suggestions when debounced query changes, but only while the
  // picker/modal is actually open. This prevents the popup from opening
  // immediately when an existing value is loaded (e.g. editing a job).
  useEffect(() => {
    if (!open) {
      setIsLoading(false);
      return;
    }
    if (debouncedQuery && debouncedQuery.trim().length >= 1) {
      fetchSuggestions(debouncedQuery);
    } else {
      setSuggestions([]);
      setIsLoading(false);
    }
  }, [debouncedQuery, fetchSuggestions, open]);

  const handleInputChange = (e) => {
    const next = e.target.value;
    setQuery(next);
    onChange && onChange(next);
    if (isValidSelection) {
      setIsValidSelection(false);
      if (typeof onValidityChange === 'function') {
        onValidityChange(false);
      }
    }
    setSelectedValue('');
    // Debouncing is handled by the useEffect with debouncedQuery
    // Just show loading state immediately
    if (next && next.trim().length >= 1) {
      setIsLoading(true);
    } else {
      setSuggestions([]);
      setHighlightIdx(-1);
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => {
    setOpen(true);
    // Always start with empty query for fresh search
    setQuery('');
    setHighlightIdx(-1);
    // Focus the input in the modal after a short delay
    setTimeout(() => {
      if (isMobile && mobileInputRef.current) {
        mobileInputRef.current.focus();
      } else if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 150);
  };

  const handleSelect = (city, event) => {
    // Prevent default to avoid input losing focus issues
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Update local state immediately
    setQuery(city);
    setSelectedValue(city);
    setIsValidSelection(true);

    // Update parent state immediately (synchronously)
    if (onChange) {
      onChange(city);
    }
    if (onValidityChange) {
      onValidityChange(true);
    }

    // Close dropdown
    setOpen(false);
    setHighlightIdx(-1);

    // Call completion callback after a brief delay to ensure state updates are processed
    if (onSelectionComplete) {
      setTimeout(() => {
        onSelectionComplete();
      }, 10);
    }

    // Blur input after a short delay to ensure state is updated
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((prev) => {
        const next = Math.min((prev + 1), suggestions.length - 1);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlightIdx >= 0 && highlightIdx < suggestions.length) {
        e.preventDefault();
        handleSelect(suggestions[highlightIdx], e);
      } else if (enforceSelection) {
        // If nothing highlighted, try to select an exact match (case-insensitive) or fallback to first suggestion
        if (suggestions && suggestions.length > 0) {
          e.preventDefault();
          const exactIdx = suggestions.findIndex(
            (s) => s.toLowerCase() === (query || '').toLowerCase()
          );
          const idxToUse = exactIdx >= 0 ? exactIdx : 0;
          handleSelect(suggestions[idxToUse], e);
        } else {
          // Prevent submitting arbitrary text via Enter when enforcement is on
          e.preventDefault();
        }
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlightIdx(-1);
    }
  };

  const handleBlur = (e) => {
    // Don't close if clicking inside the modal
    if (listRef.current && listRef.current.contains(e.relatedTarget)) {
      return;
    }
    // If enforcing selection and current text is not a valid selection,
    // accept an exact match if present, otherwise revert to last selected value
    if (enforceSelection && !isValidSelection && open) {
      const exact = suggestions.find(
        (s) => s.toLowerCase() === (query || '').toLowerCase()
      );
      if (exact) {
        handleSelect(exact, null);
      } else {
        const fallback = selectedValue || '';
        setQuery(fallback);
        const nowValid = !!fallback;
        setIsValidSelection(nowValid);

        // Update parent state immediately
        if (onChange) {
          onChange(fallback);
        }
        if (onValidityChange) {
          onValidityChange(nowValid);
        }

        // Call completion callback if valid
        if (nowValid && onSelectionComplete) {
          setTimeout(() => {
            onSelectionComplete();
          }, 0);
        }
      }
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {/* Button to open modal */}
      <button
        type="button"
        id={inputId}
        onClick={handleOpenModal}
        className="business-type-input"
        style={{
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingRight: '16px',
          background: isValidSelection ? 'rgba(31, 41, 55, 0.9)' : 'rgba(17, 24, 39, 0.8)',
          border: isValidSelection ? '2px solid #10b981' : '2px solid rgba(255, 255, 255, 0.1)'
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        required={required}
      >
        <span style={{
          color: isValidSelection ? 'var(--wolt-white)' : 'rgba(255, 255, 255, 0.6)',
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {isValidSelection && selectedValue ? selectedValue : placeholder}
        </span>
        <span style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0,
          marginLeft: '8px'
        }}>
          {isValidSelection && (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke={isValidSelection ? '#10b981' : 'rgba(255, 255, 255, 0.5)'}
            strokeWidth="2"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      {/* Modal with search input */}
      {open && (() => {
        if (isMobile) {
          // Calculate available height above keyboard using visual viewport
          const availableHeight = viewportHeight || window.innerHeight;
          const sheetMaxHeight = Math.min(availableHeight * 0.85, 600);

          const sheet = (
            <div
              ref={listRef}
              style={{
                position: 'fixed',
                top: `${viewportOffset.top}px`,
                left: `${viewportOffset.left}px`,
                width: window.visualViewport ? `${window.visualViewport.width}px` : '100%',
                height: `${availableHeight}px`,
                zIndex: 2147483647,
                display: 'flex',
                alignItems: 'flex-end',
                overscrollBehaviorY: 'contain',
                pointerEvents: 'auto'
              }}
              aria-hidden={false}
            >
              <div
                onClick={() => { setOpen(false); setHighlightIdx(-1); }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0,0,0,0.4)',
                  backdropFilter: 'blur(2px)',
                  transition: 'opacity 0.2s ease'
                }}
              />
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  background: 'white',
                  borderTopLeftRadius: 20,
                  borderTopRightRadius: 20,
                  boxShadow: '0 -4px 24px rgba(0,0,0,0.2)',
                  maxHeight: `${sheetMaxHeight}px`,
                  paddingTop: 12,
                  paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  animation: 'slideUp 0.3s ease-out'
                }}
              >
                <style>{`
                  @keyframes slideUp {
                    from {
                      transform: translateY(100%);
                    }
                    to {
                      transform: translateY(0);
                    }
                  }
                `}</style>
                <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 12 }}>
                  <div style={{
                    width: 40,
                    height: 4,
                    borderRadius: 999,
                    background: '#d1d5db',
                    cursor: 'grab'
                  }} />
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 20px 16px 20px',
                    borderBottom: '1px solid #f3f4f6'
                  }}
                >
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>Choose your city</div>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => { setOpen(false); setHighlightIdx(-1); }}
                    style={{
                      appearance: 'none',
                      border: 'none',
                      background: '#f3f4f6',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#374151',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#e5e7eb';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#f3f4f6';
                    }}
                  >
                    Close
                  </button>
                </div>
                {/* Search input in mobile sheet */}
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      ref={mobileInputRef}
                      type="text"
                      value={query}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      placeholder="Type to search cities..."
                      style={{
                        width: '100%',
                        padding: '14px 16px 14px 44px',
                        fontSize: 16,
                        border: '2px solid #e5e7eb',
                        borderRadius: 12,
                        background: 'white',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1), 0 2px 4px rgba(0,0,0,0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                        handleBlur(e);
                      }}
                      autoFocus
                    />
                    <div style={{
                      position: 'absolute',
                      left: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none'
                    }}>
                      {isLoading ? (
                        <div style={{
                          width: 18,
                          height: 18,
                          border: '2px solid #e5e7eb',
                          borderTopColor: '#2563eb',
                          borderRadius: '50%',
                          animation: 'spin 0.6s linear infinite'
                        }} />
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                          <circle cx="11" cy="11" r="8" />
                          <path d="m21 21-4.35-4.35" />
                        </svg>
                      )}
                    </div>
                  </div>
                  {isLoading && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 8,
                      color: '#6b7280',
                      fontSize: 14
                    }}>
                      <div style={{
                        width: 16,
                        height: 16,
                        border: '2px solid #e5e7eb',
                        borderTopColor: '#2563eb',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite'
                      }} />
                      <span>Searching...</span>
                    </div>
                  )}
                </div>
                <ul
                  id={`${inputId}-listbox`}
                  role="listbox"
                  style={{
                    margin: 0,
                    padding: '8px 12px',
                    listStyle: 'none',
                    overflowY: 'auto',
                    flex: 1,
                    minHeight: 0,
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {isLoading && suggestions.length === 0 ? (
                    <li style={{
                      padding: '24px 16px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: 15
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 24,
                          height: 24,
                          border: '3px solid #e5e7eb',
                          borderTopColor: '#2563eb',
                          borderRadius: '50%',
                          animation: 'spin 0.6s linear infinite'
                        }} />
                        <span>Searching cities...</span>
                      </div>
                    </li>
                  ) : suggestions.length === 0 && query.trim().length >= 1 ? (
                    <li style={{ padding: '24px 16px', color: '#6b7280', fontSize: 15, textAlign: 'center' }}>
                      No cities found. Try a different search.
                    </li>
                  ) : suggestions.length === 0 ? (
                    <li style={{ padding: '24px 16px', color: '#6b7280', fontSize: 15, textAlign: 'center' }}>
                      Start typing to search cities...
                    </li>
                  ) : (
                    suggestions.map((s, idx) => (
                      <li
                        key={`${s}-${idx}`}
                        role="option"
                        aria-selected={idx === highlightIdx}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelect(s, e);
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelect(s, e);
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelect(s, e);
                        }}
                        onMouseEnter={() => setHighlightIdx(idx)}
                        style={{
                          padding: '16px 16px',
                          minHeight: 56,
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: 17,
                          fontWeight: idx === highlightIdx ? 600 : 400,
                          cursor: 'pointer',
                          background: idx === highlightIdx ? '#eff6ff' : 'transparent',
                          borderRadius: 12,
                          marginBottom: 4,
                          transition: 'all 0.15s ease',
                          border: idx === highlightIdx ? '2px solid #2563eb' : '2px solid transparent',
                          transform: idx === highlightIdx ? 'scale(1.02)' : 'scale(1)'
                        }}
                      >
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          width: '100%'
                        }}>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke={idx === highlightIdx ? '#2563eb' : '#9ca3af'}
                            strokeWidth="2"
                            style={{ flexShrink: 0 }}
                          >
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          {s}
                        </span>
                      </li>
                    ))
                  )}
                </ul>
                <style>{`
                  @keyframes spin {
                    to { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            </div>
          );
          return createPortal(sheet, document.body);
        }
        // Desktop modal
        return (
          <div
            ref={listRef}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2147483647,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setOpen(false);
                setHighlightIdx(-1);
              }
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: 16,
                width: '90%',
                maxWidth: 500,
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
                animation: 'slideUp 0.3s ease-out',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <style>{`
                @keyframes fadeIn {
                  from { opacity: 0; }
                  to { opacity: 1; }
                }
                @keyframes slideUp {
                  from {
                    opacity: 0;
                    transform: translateY(20px) scale(0.95);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                  }
                }
              `}</style>
              {/* Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '20px 24px',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>Choose your city</div>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => { setOpen(false); setHighlightIdx(-1); }}
                  style={{
                    appearance: 'none',
                    border: 'none',
                    background: '#f3f4f6',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f3f4f6';
                  }}
                >
                  Close
                </button>
              </div>
              {/* Search input */}
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
                <div style={{ position: 'relative' }}>
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Type to search cities..."
                    style={{
                      width: '100%',
                      padding: '14px 16px 14px 44px',
                      fontSize: 16,
                      border: '2px solid #e5e7eb',
                      borderRadius: 12,
                      background: 'white',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1), 0 2px 4px rgba(0,0,0,0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                      handleBlur(e);
                    }}
                    autoFocus
                  />
                  <div style={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                  }}>
                    {isLoading ? (
                      <div style={{
                        width: 18,
                        height: 18,
                        border: '2px solid #e5e7eb',
                        borderTopColor: '#2563eb',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite'
                      }} />
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
              {/* Results list */}
              <ul
                id={`${inputId}-listbox`}
                role="listbox"
                style={{
                  margin: 0,
                  padding: '12px',
                  listStyle: 'none',
                  overflowY: 'auto',
                  flex: 1,
                  minHeight: 0,
                  maxHeight: '50vh'
                }}
              >
            <style>{`
              @keyframes fadeInDown {
                from {
                  opacity: 0;
                  transform: translateY(-8px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}</style>
            {isLoading && suggestions.length === 0 ? (
              <li style={{
                padding: '20px 16px',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: 14
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 20,
                    height: 20,
                    border: '2px solid #e5e7eb',
                    borderTopColor: '#2563eb',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite'
                  }} />
                  <span>Searching cities...</span>
                </div>
              </li>
            ) : suggestions.length === 0 && query.trim().length >= 1 ? (
              <li style={{
                padding: '20px 16px',
                color: '#6b7280',
                fontSize: 14,
                textAlign: 'center'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <span>No cities found. Try a different search.</span>
                </div>
              </li>
            ) : suggestions.length === 0 ? (
              <li style={{
                padding: '20px 16px',
                color: '#6b7280',
                fontSize: 14,
                textAlign: 'center'
              }}>
                Start typing to search cities...
              </li>
            ) : (
              suggestions.map((s, idx) => (
                <li
                  key={`${s}-${idx}`}
                  role="option"
                  aria-selected={idx === highlightIdx}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(s, e);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelect(s, e);
                  }}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  style={{
                    padding: '12px 14px',
                    cursor: 'pointer',
                    background: idx === highlightIdx ? '#eff6ff' : 'transparent',
                    borderRadius: 8,
                    marginBottom: 2,
                    transition: 'all 0.15s ease',
                    border: idx === highlightIdx ? '2px solid #2563eb' : '2px solid transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontWeight: idx === highlightIdx ? 600 : 400,
                    color: idx === highlightIdx ? '#1e40af' : '#111827',
                    transform: idx === highlightIdx ? 'translateX(4px)' : 'translateX(0)'
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={idx === highlightIdx ? '#2563eb' : '#9ca3af'}
                    strokeWidth="2"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>{s}</span>
                  {idx === highlightIdx && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#2563eb"
                      strokeWidth="3"
                      style={{ marginLeft: 'auto', flexShrink: 0 }}
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </li>
              ))
            )}
                <style>{`
                  @keyframes spin {
                    to { transform: rotate(360deg); }
                  }
                `}</style>
              </ul>
            </div>
          </div>
        );
      })()}
    </div>
  );
}



